import React, { useState } from 'react'
import { Autocomplete, Box, Checkbox, Chip, TextField } from '@mui/material'
import { IconSeeker } from "@ui-seeker/core"

import { dsColorNew } from "@palette"

/**
 * Grouped multi-select. Options come in as a flat list; a `groupBy`
 * function decides which parent bucket each one belongs to. The header
 * of each group behaves as a select-all checkbox for that bucket.
 *
 * Props:
 *   label
 *   options        [{...}]
 *   value          [id]        currently selected ids
 *   getOptionId    fn(opt)     unique id for this option
 *   getOptionLabel fn(opt)     display label for the option
 *   groupBy        fn(opt)     group name for this option
 *   onChange       fn(ids)     new selection, emits with intent='pending'
 *   sx             MUI sx
 *   minWidth
 */
export default function GroupedMultiSelect({
	label, options, value, getOptionId, getOptionLabel, groupBy,
	onChange, sx, minWidth = 260,
	renderTag,  // optional: (option, tagProps) => Chip
	defaultCollapsed = true,
}) {
	const selectedSet = new Set(value)
	const selectedOptions = options.filter(o => selectedSet.has(getOptionId(o)))

	// group name → array of option ids in that group
	const idsByGroup = new Map()
	for (const o of options) {
		const g = groupBy(o) || '—'
		if (!idsByGroup.has(g)) idsByGroup.set(g, [])
		idsByGroup.get(g).push(getOptionId(o))
	}

	// MUI needs the options of each group together, or it repeats the group header;
	// keep the groups in the order they first appear and the options as they came
	const groupedOptions = []
	const optionsByGroup = new Map()
	for (const o of options) {
		const g = groupBy(o) || '—'
		if (!optionsByGroup.has(g)) optionsByGroup.set(g, [])
		optionsByGroup.get(g).push(o)
	}
	optionsByGroup.forEach(list => groupedOptions.push(...list))

	// Track which groups are expanded. Default = collapsed for
	// compactness; the user opens the ones they want to browse.
	const [expanded, setExpanded] = useState(() => new Set(
		defaultCollapsed ? [] : Array.from(idsByGroup.keys())
	))
	const toggleExpanded = (group) => {
		setExpanded(prev => {
			const next = new Set(prev)
			if (next.has(group)) next.delete(group)
			else                 next.add(group)
			return next
		})
	}

	const toggleGroup = (group) => {
		const groupIds = idsByGroup.get(group) || []
		const allIn = groupIds.every(id => selectedSet.has(id))
		let next
		if (allIn) {
			next = value.filter(id => !groupIds.includes(id))
		} else {
			const merged = new Set(value)
			groupIds.forEach(id => merged.add(id))
			next = Array.from(merged)
		}
		onChange(next, { intent: 'pending' })
	}

	return (
		<Autocomplete
			multiple
			disableCloseOnSelect
			size="small"
			options={groupedOptions}
			value={selectedOptions}
			groupBy={groupBy}
			getOptionLabel={getOptionLabel}
			isOptionEqualToValue={(a, b) => getOptionId(a) === getOptionId(b)}
			onChange={(_, picked) =>
				onChange(picked.map(getOptionId), { intent: 'pending' })}
			renderOption={(props, option, { selected }) => (
				<li {...props} key={getOptionId(option)}>
					<Checkbox
						checked={selected}
						sx={{ mr: 1, p: 0.25 }}/>
					<Box component="span" sx={{ fontSize: 14 }}>
						{getOptionLabel(option)}
					</Box>
				</li>
			)}
			renderGroup={(params) => {
				const groupIds = idsByGroup.get(params.group) || []
				const nSel = groupIds.filter(id => selectedSet.has(id)).length
				const state = nSel === 0 ? 'none' : (nSel === groupIds.length ? 'all' : 'some')
				const isOpen = expanded.has(params.group)
				const stop = (e) => { e.preventDefault(); e.stopPropagation() }
				return (
					<li key={params.key}>
						<Box
							onMouseDown={(e) => { stop(e); toggleExpanded(params.group) }}
							sx={{
								display: 'flex', alignItems: 'center', gap: 0.5,
								position: 'sticky', top: 0, zIndex: 1,
								bgcolor: dsColorNew.white,
								borderBottom: '1px solid', borderColor: dsColorNew.grayBorder,
								px: 0.5, py: 0.5, cursor: 'pointer',
								'&:hover': { bgcolor: dsColorNew.hoverBgLight },
							}}>
							<IconSeeker icon={isOpen ? 'chevron-down' : 'chevron-right'} sx={{ fontSize: 12, width: 20, textAlign: 'center' }}/>
							<Box
								onMouseDown={(e) => { stop(e); toggleGroup(params.group) }}
								sx={{ display: 'flex', alignItems: 'center', gap: 0.75,
								      flex: 1 }}>
								{/* The group's state, drawn as the app's checkbox (the click is the row's) */}
								<Checkbox size="small" checked={state === 'all'} indeterminate={state === 'some'} tabIndex={-1} disableRipple sx={{ p: 0.25, pointerEvents: 'none' }}/>
								<Box component="span" sx={{ fontWeight: 700, fontSize: 13,
								                            letterSpacing: '0.02em' }}>
									{params.group}
								</Box>
								<Box component="span" sx={{ ml: 'auto', fontSize: 12,
								                            color: dsColorNew.textMuted }}>
									{nSel}/{groupIds.length}
								</Box>
							</Box>
						</Box>
						{isOpen && (
							<ul style={{ padding: 0, margin: 0 }}>{params.children}</ul>
						)}
					</li>
				)
			}}
			renderTags={(picked, getTagProps) =>
				picked.map((o, i) => renderTag
					? renderTag(o, getTagProps({ index: i }))
					: (
						<Chip {...getTagProps({ index: i })} key={getOptionId(o)}
						      size="small" label={getOptionLabel(o)}/>
					))
			}
			renderInput={(params) => <TextField {...params} InputLabelProps={{ ...params.InputLabelProps, shrink: true }} label={label}/>}
			sx={{ minWidth, ...sx }}
		/>
	)
}
